"use client";

import React, { useState, useEffect, useRef } from "react";
import { Truck, ThermometerSnowflake, ShieldAlert, MapPin, Check, X, Ruler, Weight, Box, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const fleetData = [{
  id: 1,
  name: "Counter-Pressure Canning",
  type: "Carbonated Beverage Filling Line",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/canning_line_tech.png",
  payload: "3,000 cans/hour",
  dimensions: "202 - 300 Diameter Cans",
  volume: "Up to 5.0 Vol CO2",
  hasGps: true,
  hasAdr: true,
  hasTemp: true,
  hasLift: true
}, {
  id: 2,
  name: "Precision Formulation Tank",
  type: "Automated Mixing & Infusion",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/formulation_tank_tech.png",
  payload: "2,500 L Batch Size",
  dimensions: "316L Stainless Steel",
  volume: "High-Shear Agitation",
  hasGps: true,
  hasAdr: true,
  hasTemp: true,
  hasLift: false
}, {
  id: 3,
  name: "Flash Pasteurization Unit",
  type: "Thermal Microbial Control",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/pasteurizer_tech.png",
  payload: "Continuous Flow",
  dimensions: "Plate Heat Exchanger",
  volume: "Full Log-Reduction",
  hasGps: true,
  hasAdr: true,
  hasTemp: true,
  hasLift: true
}];
const featuresList = [{
  key: "hasGps",
  label: "FDA Registered Facility",
  icon: MapPin
}, {
  key: "hasAdr",
  label: "GMP Certified Protocols",
  icon: ShieldAlert
}, {
  key: "hasTemp",
  label: "Precision Thermal Control",
  icon: ThermometerSnowflake
}, {
  key: "hasLift",
  label: "Integrated Logistics Support",
  icon: Truck
}];
export default function FleetCapabilities() {
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="158540" id="technical-specs" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] bg-secondary/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-mono font-medium mb-4 uppercase tracking-widest">
              <Truck className="size-4" />
              <span>Production Infrastructure</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tighter mb-4 font-sans">
              Technical Specs
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our FDA-registered facility utilizes medical-grade equipment for precision carbonation and formulation. 
              Explore the engineering standards that power our contract manufacturing capabilities.
            </p>
          </div>

          {}
          <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm border border-border p-2 rounded-full shadow-sm shrink-0">
            <span className={`text-xs font-mono font-bold transition-colors uppercase tracking-wider px-2 ${!isComparisonMode ? 'text-primary' : 'text-muted-foreground'}`}>
              Overview
            </span>
            <Switch checked={isComparisonMode} onCheckedChange={setIsComparisonMode} aria-label="Toggle comparison mode" className="data-[state=checked]:bg-primary" />
            <span className={`text-xs font-mono font-bold transition-colors uppercase tracking-wider px-2 ${isComparisonMode ? 'text-primary' : 'text-muted-foreground'}`}>
              Full Specs
            </span>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {fleetData.map((vehicle, i) => <Card key={vehicle.id} data-index={i} className="overflow-hidden border-border/40 shadow-xl hover:shadow-2xl transition-all duration-500 group flex flex-col rounded-xl bg-card/80 backdrop-blur-sm">
              {}
              <div className="relative h-56 bg-muted overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent z-10" />
                <img src={vehicle.image} alt={vehicle.name} className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute bottom-4 left-4 z-20">
                  <h3 className="text-xl font-bold text-white tracking-tight">{vehicle.name}</h3>
                  <p className="text-xs font-mono text-primary uppercase tracking-widest">{vehicle.type}</p>
                </div>
              </div>

              <CardContent className="p-8 flex-grow">
                {!isComparisonMode ? <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Weight className="size-4 text-primary" />
                          <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Throughput</span>
                        </div>
                        <p className="font-semibold text-foreground text-lg">{vehicle.payload}</p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-muted-foreground mb-1">
                          <Box className="size-4 text-primary" />
                          <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Efficiency</span>
                        </div>
                        <p className="font-semibold text-foreground text-lg">{vehicle.volume}</p>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-border/50 space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground mb-1">
                        <Ruler className="size-4 text-primary" />
                        <span className="text-[10px] font-mono font-bold uppercase tracking-widest">Unit Compatibility</span>
                      </div>
                      <p className="font-medium text-foreground">{vehicle.dimensions}</p>
                    </div>
                  </div> : <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h4 className="text-[10px] font-mono font-bold text-primary uppercase tracking-widest mb-4">Compliance & Systems</h4>
                    <ul className="space-y-3">
                      {featuresList.map((feature, index) => {
                  const hasFeature = vehicle[feature.key] as boolean;
                  const Icon = feature.icon;
                  return <li key={index} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                            <div className="flex items-center gap-3">
                              <div className={`p-1.5 rounded-full ${hasFeature ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                <Icon className="size-4" />
                              </div>
                              <span className={`text-sm ${hasFeature ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                {feature.label}
                              </span>
                            </div>
                            <div>
                              {hasFeature ? <Check className="size-5 text-primary" /> : <X className="size-5 text-muted-foreground/30" />}
                            </div>
                          </li>;
                })}
                    </ul>
                  </div>}
              </CardContent>
              
              <CardFooter className="p-8 pt-0 mt-auto">
                <Button variant="outline" className="w-full group/btn rounded-full border-primary/30 hover:bg-primary/10 hover:border-primary transition-all duration-300" asChild>
                  <Link to="/ContactPage">
                    Reserve Production Slot
                    <ArrowRight className="size-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        {}
        <div className="mt-20 text-center">
          <p className="text-muted-foreground mb-8 text-lg font-sans">
            Ready to scale your beverage brand with precision-engineered contract manufacturing?
          </p>
          <Button size="lg" className="rounded-full px-10 py-6 text-base font-bold tracking-tight bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all duration-300" asChild>
            <Link to="/ContactPage">
              Request a Technical Consultation
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}